import Image from '~/components/common/Image'
import { FormattedMessage } from 'react-intl'
import clsx from 'classnames'
import { useCallback, useEffect, useRef, useState } from 'react'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'

import type { CatalogProductType } from '~/presets/catalogPresets'
import styles from './ProductCategoryTabs.module.css'

interface CategoryTab {
  type: CatalogProductType
  nameId: string
  image: string
}

const categories: CategoryTab[] = [
  {
    type: 'wardrobe',
    nameId: 'homepage.products.dulap',
    image: '/cdn/renders-landing/wardrobe.png',
  },
  {
    type: 'stand',
    nameId: 'homepage.products.stand',
    image: '/cdn/renders-landing/stand.png',
  },
  {
    type: 'tv-stand',
    nameId: 'homepage.products.TVstand',
    image: '/cdn/renders-landing/tv-stand.png',
  },
  {
    type: 'bedside',
    nameId: 'homepage.products.bedside',
    image: '/cdn/renders-landing/bedside.png',
  },
  {
    type: 'table',
    nameId: 'homepage.products.table',
    image: '/cdn/renders-landing/table.jpg',
  },
  {
    type: 'work-table',
    nameId: 'homepage.products.work-table',
    image: '/cdn/work-table/work-table.jpg',
  },
  // {
  //   type: 'rack',
  //   nameId: 'homepage.products.rack',
  //   image: '/renders-landing/rack.png',
  // },
]

interface Props {
  selected: CatalogProductType
  onSelect: (type: CatalogProductType) => void
}

export const ProductCategoryTabs: React.FC<Props> = ({
  selected,
  onSelect,
}) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    setCanScrollLeft(el.scrollLeft > 4)
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4)
  }, [])

  useEffect(() => {
    updateScrollState()
    window.addEventListener('resize', updateScrollState)
    return () => window.removeEventListener('resize', updateScrollState)
  }, [updateScrollState])

  const handleScrollBy = (direction: -1 | 1) => {
    const el = scrollRef.current
    if (!el) return
    const amount = el.clientWidth * 0.6 * direction
    el.scrollBy({ left: amount, behavior: 'smooth' })
  }

  return (
    <div className={styles.wrapper}>
      <button
        type="button"
        className={clsx(
          styles.arrow,
          styles.arrowLeft,
          !canScrollLeft && styles.arrowHidden
        )}
        onClick={() => handleScrollBy(-1)}
        aria-label="Scroll tabs left"
        tabIndex={canScrollLeft ? 0 : -1}
      >
        <ChevronLeftIcon />
      </button>

      <div
        ref={scrollRef}
        className={styles.tabsContainer}
        onScroll={updateScrollState}
      >
        {categories.map((cat) => (
          <button
            key={cat.type}
            className={clsx(
              styles.tab,
              selected === cat.type && styles.tabActive
            )}
            onClick={() => onSelect(cat.type)}
          >
            <div className={styles.imageWrap}>
              <Image
                src={cat.image}
                alt=""
                width={400}
                height={200}
                className={styles.tabImage}
                unoptimized={cat.image.startsWith('/cdn/renders-landing/')}
              />
            </div>
            <span className={styles.tabLabel}>
              <FormattedMessage id={cat.nameId} />
            </span>
          </button>
        ))}
      </div>

      <button
        type="button"
        className={clsx(
          styles.arrow,
          styles.arrowRight,
          !canScrollRight && styles.arrowHidden
        )}
        onClick={() => handleScrollBy(1)}
        aria-label="Scroll tabs right"
        tabIndex={canScrollRight ? 0 : -1}
      >
        <ChevronRightIcon />
      </button>
    </div>
  )
}
